import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCommandRegistry } from "../../../contexts/CommandRegistryContext";
import { getSpeechProvider } from "../../../services/speech/speechProviders";
import { VOICE_UNSUPPORTED } from "../../../services/speech/speech.copy";

import { SUPPORT_URL, useHelpSupport } from "../../../hooks/useHelpSupport";
/** Release notes page, set per deployment. Unset: the entry stays a teaser. */
const WHATS_NEW_URL = process.env.REACT_APP_WHATS_NEW_URL || undefined;
/** Docs and tutorials page, set per deployment. Unset: the entry stays a teaser. */
const DOCS_URL = process.env.REACT_APP_DOCS_URL || undefined;

export function useHelpMenuHostInternalState() {
  const registry = useCommandRegistry();
  const help = useHelpSupport();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((value) => !value), []);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const voiceSupported = useMemo(() => getSpeechProvider().isSupported(), []);

  const onSelect = useCallback(
    (id: string) => {
      setOpen(false);
      if (id === "shortcuts") registry.ui.setShortcutsOpen(true);
      // The command list has the mic (services/speech); opening it is the voice entry.
      if (id === "voice") registry.ui.setPaletteOpen(true);
      if (id === "live-chat") help.openLiveChat();
      if (id === "feedback") help.openFeedback();
    },
    [registry, help],
  );

  return {
    open,
    toggle,
    close,
    wrapperRef,
    onSelect,
    voiceDisabledReason: voiceSupported ? undefined : VOICE_UNSUPPORTED,
    supportUrl: SUPPORT_URL,
    liveChatAvailable: help.liveChatAvailable,
    whatsNewUrl: WHATS_NEW_URL,
    docsUrl: DOCS_URL,
  };
}
